import React from 'react';
import { Snackbar, Alert, Button, Slide } from '@mui/material';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import ErrorIcon from '@mui/icons-material/Error';
import WarningIcon from '@mui/icons-material/Warning';
import InfoIcon from '@mui/icons-material/Info';
import { colors } from '../../../styles/theme';

export type ToastSeverity = 'success' | 'error' | 'warning' | 'info';

interface ToastProps {
    open: boolean;
    message: string;
    severity?: ToastSeverity;
    duration?: number;
    /** Knopf in der Meldung, etwa „Rückgängig“ – schließt die Meldung beim Klick */
    action?: ToastAction;
    onClose: () => void;
}

export interface ToastAction {
    label: string;
    onClick: () => void;
}

/**
 * Wiederverwendbare Toast-Komponente mit Glassy-Design
 * 
 * Features:
 * - Verschiedene Severity-Typen (success, error, warning, info)
 * - Glassy-Stil mit Backdrop-Filter
 * - Automatisches Ausblenden nach konfigurierbarer Dauer
 * - Slide-Animation von unten
 */
const Toast: React.FC<ToastProps> = ({
    open,
    message,
    severity = 'success',
    duration = 2000,
    action,
    onClose,
}) => {
    const getColor = () => {
        switch (severity) {
            case 'success': return colors.status.success.main;
            case 'error': return colors.status.error.main;
            case 'warning': return colors.status.warning.main;
            case 'info': return colors.status.info.main;
            default: return colors.status.success.main;
        }
    };

    const getIcon = () => {
        switch (severity) {
            case 'success': return <CheckCircleIcon fontSize="inherit" />;
            case 'error': return <ErrorIcon fontSize="inherit" />;
            case 'warning': return <WarningIcon fontSize="inherit" />;
            case 'info': return <InfoIcon fontSize="inherit" />;
            default: return <CheckCircleIcon fontSize="inherit" />;
        }
    };

    return (
        <Snackbar
            open={open}
            autoHideDuration={duration}
            onClose={onClose}
            anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
            slots={{ transition: Slide }}
            slotProps={{ transition: { direction: 'up' } as any }}
        >
            <Alert
                icon={getIcon()}
                severity={severity}
                action={action && (
                    <Button
                        size="small"
                        onClick={() => {
                            action.onClick();
                            onClose();
                        }}
                        sx={{ color: getColor(), fontWeight: 700, textTransform: 'none' }}
                    >
                        {action.label}
                    </Button>
                )}
                sx={{
                    // Glassy Styles
                    background: colors.glass.background,
                    backdropFilter: 'blur(10px)',
                    WebkitBackdropFilter: 'blur(10px)',
                    border: `1px solid ${colors.glass.borderStrong}`,
                    borderRadius: '15px',
                    boxShadow: `0 8px 32px ${colors.glass.borderSubtle}, inset 0 1px 0 ${colors.glass.inset}, 0 1px 3px ${colors.shadow.heavy}`,
                    color: getColor(),
                    fontWeight: 600,
                    '& .MuiAlert-icon': {
                        color: getColor(),
                    },
                    '& .MuiAlert-message': {
                        fontWeight: 600,
                    }
                }}
            >
                {message}
            </Alert>
        </Snackbar>
    );
};

export default Toast;




